import type { Directive } from 'vue';

import { useUserManagementStore } from '@/stores/agency/settings/user-management/user-management';

export const mobileNumber: Directive = {
  mounted(el: HTMLElement) {
    nextTick(() => {
      const input = el.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null;

      if (!input) {
        console.warn('No input/textarea found for phoneNumber directive');

        return;
      }

      const handleKeydown = (e: KeyboardEvent) => {
        const isDigit = /^[0-9]$/.test(e.key);
        const isPlus = e.key === '+';

        if (isDigit) return;

        if (isPlus) {
          const alreadyHasPlus = input.value.startsWith('+');
          const firstCharIsPlus = input.value?.[0] === '+';

          if (input.value?.length === 0) {
            return;
          }

          if (!alreadyHasPlus && !firstCharIsPlus) {
            return;
          }

          e.preventDefault();

          return;
        }

        // allow navigation for edit
        if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(e.key)) {
          return;
        }

        // ❌ Block everything else
        e.preventDefault();
      };

      // Clean pasted/invalid content
      const handleInput = () => {
        let value = input.value;

        if (value.startsWith('+')) {
          value = `+${value.slice(1).replace(/[^0-9]/g, '')}`;
        } else {
          value = value.replace(/[^0-9]/g, '');
        }

        if (input.value !== value) {
          const cursorPos = input.selectionStart || 0;

          input.value = value;

          const newCursorPos = Math.min(cursorPos, value.length);

          input.setSelectionRange(newCursorPos, newCursorPos);
          input.dispatchEvent(new Event('input'));
        }
      };

      input.addEventListener('keydown', (e: Event) => {
        const keyboardEvent = e as KeyboardEvent;

        handleKeydown(keyboardEvent);
      });
      input.addEventListener('input', handleInput);

      // Store cleanup on element
      (el as any)._cleanup = () => {
        input.removeEventListener('keydown', (e: Event) => {
          const keyboardEvent = e as KeyboardEvent;

          handleKeydown(keyboardEvent);
        });
        input.removeEventListener('input', handleInput);
      };
    });
  },

  unmounted(el: any) {
    el._cleanup?.();
  }
};

export const allowedText: Directive = {
  mounted(el: HTMLElement) {
    nextTick(() => {
      const input = el.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null;

      if (!input) {
        console.warn('No input/textarea found for allowedText directive');

        return;
      }

      const isTextarea = input.tagName === 'TEXTAREA';

      // ✅ allowed char: alphanumeric, comma, hyphen, parens, forward slash, & ? [ ] + @ % # ., space
      const allowedPattern = /^[a-zA-Z0-9,\-\(\)\/&?\[\]+@%#. ]$/;
      const specialCharPattern = /^[,\-\/&?\[\]+@%#()]$/;
      const disallowedCharsRegex = /[^a-zA-Z0-9,\-\(\)\/&?\[\]+@%#. \n]/g;

      const handleKeydown = (e: KeyboardEvent) => {
        const key = e.key;

        // allow navigation and keyboard shortcuts
        if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(key)) {
          return;
        }

        // allow line breaks in a textarea
        if (isTextarea && key === 'Enter') {
          return;
        }

        // ❌ Block disallowed characters
        if (!allowedPattern.test(key)) {
          e.preventDefault();

          return;
        }

        // ❌ Block consecutive same special char (e.g. @@ or --)
        if (specialCharPattern.test(key)) {
          const cursorPos = input.selectionStart || 0;
          if (input.value[cursorPos - 1] === key) {
            e.preventDefault();
          }
        }
      };

      const handleInput = () => {
        let value = input.value;

        // Step 1: remove disallowed characters
        value = value.replace(disallowedCharsRegex, '');

        // Step 2: collapse consecutive identical special chars into one (handles paste)
        value = value.replace(/([,\-\/&?\[\]+@%#().`])\1+/g, '$1');

        // Step 3: prevent starting with special char
        value = value.replace(/^[,\-\/&?\[\]+@%#().]+/, '');

        if (input.value !== value) {
          const cursorPos = input.selectionStart || 0;

          input.value = value;

          const newCursorPos = Math.min(cursorPos, value.length);

          input.setSelectionRange(newCursorPos, newCursorPos);
          input.dispatchEvent(new Event('input'));
        }
      };

      input.addEventListener('keydown', (e: Event) => {
        const keyboardEvent = e as KeyboardEvent;

        handleKeydown(keyboardEvent);
      });
      input.addEventListener('input', handleInput);

      (el as any)._cleanup = () => {
        input.removeEventListener('keydown', (e: Event) => {
          const keyboardEvent = e as KeyboardEvent;

          handleKeydown(keyboardEvent);
        });
        input.removeEventListener('input', handleInput);
      };
    });
  },
  unmounted(el: any) {
    el._cleanup?.();
  }
};

export const decimalLimit: Directive = {
  mounted(el: HTMLElement, binding) {
    nextTick(() => {
      const input = el.querySelector('input') as HTMLInputElement | null;

      if (!input) {
        console.warn('No input found for decimalLimit directive');

        return;
      }

      const maxDecimals = typeof binding.value === 'number' ? binding.value : 2;

      // Deliberately skip cursor-position handling (unlike the other directives here) —
      // input[type=number] doesn't support selectionStart/setSelectionRange in most browsers.
      const handleInput = () => {
        const value = input.value;
        const dotIndex = value.indexOf('.');

        if (dotIndex === -1) {
          return;
        }

        const decimalDigits = value.length - dotIndex - 1;

        if (decimalDigits <= maxDecimals) {
          return;
        }

        input.value = value.slice(0, dotIndex + 1 + maxDecimals);
        input.dispatchEvent(new Event('input'));
      };

      input.addEventListener('input', handleInput);

      (el as any)._cleanupDecimalLimit = () => {
        input.removeEventListener('input', handleInput);
      };
    });
  },

  unmounted(el: any) {
    el._cleanupDecimalLimit?.();
  }
};

export const hasPermission: Directive = {
  mounted(el, binding) {
    const store = useUserManagementStore();

    const required = Array.isArray(binding.value) ? binding.value : [binding.value];

    const hasAll = required.every((key: string) => store.userHasPermission(key));

    el.style.setProperty('display', hasAll ? '' : 'none', 'important');
  },

  updated(el, binding) {
    const store = useUserManagementStore();

    const required = Array.isArray(binding.value) ? binding.value : [binding.value];

    const hasAll = required.every((key: string) => store.userHasPermission(key));

    el.style.setProperty('display', hasAll ? '' : 'none', 'important');
  }
};
